import { Container } from "@react-three/uikit";
import type { ReactNode } from "react";

/** Audio configuration for a channel */
export interface ChannelAudioConfig {
  /** Audio source URL (supports mp3, wav, ogg, etc.) */
  src: string;
  /** Whether to loop the audio (default: false) */
  loop?: boolean;
  /** Base volume level 0-1, combined with TV volume control (default: 1) */
  volume?: number;
}

export interface ScreenChannelProps {
  /** Unique channel number/identifier */
  channel: number;
  /** Optional channel name/label */
  name?: string;
  /** Content to display when this channel is active */
  children?: ReactNode;
  visible?: boolean;
  /** If true, automatically sets name to "No Signal" and keeps overlay visible */
  noSignal?: boolean;
  /** If true, disables the channel overlay when switching to this channel */
  noOverlay?: boolean;
  /**
   * Audio to play when this channel is active.
   * Can be a URL string or a config object for more control.
   * Audio respects the TV's volume control and power state.
   *
   * @example
   * ```tsx
   * // Simple usage
   * <CRTScreen.Channel channel={1} audio="/sounds/background.mp3">
   *
   * // With config
   * <CRTScreen.Channel channel={1} audio={{ src: "/sounds/bg.mp3", loop: true, volume: 0.8 }}>
   * ```
   */
  audio?: string | ChannelAudioConfig;
}

/**
 * CRTScreen.Channel - Defines a TV channel with content
 *
 * Wrap your content in Channel components to create switchable channels.
 * The channel prop determines the channel number (used for display and switching).
 *
 * @example
 * ```tsx
 * <CRTScreen.Root>
 *   <CRTScreen.Channel channel={1} name="Main">
 *     <CRTScreen.Content>
 *       <HomePage />
 *     </CRTScreen.Content>
 *   </CRTScreen.Channel>
 *   <CRTScreen.Channel channel={2} name="Settings">
 *     <CRTScreen.Content>
 *       <SettingsPage />
 *     </CRTScreen.Content>
 *   </CRTScreen.Channel>
 * </CRTScreen.Root>
 * ```
 */
export function ScreenChannel({ children }: ScreenChannelProps) {
  return (
    <Container
      width="100%"
      height="100%"
      backgroundColor="#000000"
      positionType="relative"
    >
      {children}
    </Container>
  );
}
